import { useQuery } from "@tanstack/react-query"
import type {
  UseBaseQueryOptions,
  UseQueryResult,
  QueryKey,
  WithRequired,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  FindTypeKeysQueryResponse,
  FindTypeKeysQueryParams,
} from "../types/FindTypeKeys"

type FindTypeKeysClient = typeof client<FindTypeKeysQueryResponse, Error, never>
type FindTypeKeys = {
  data: FindTypeKeysQueryResponse
  error: Error
  request: never
  pathParams: never
  queryParams: FindTypeKeysQueryParams
  headerParams: never
  response: Awaited<ReturnType<FindTypeKeysClient>>
  client: {
    parameters: Partial<Parameters<FindTypeKeysClient>[0]>
    return: Awaited<ReturnType<FindTypeKeysClient>>
  }
}
export const findTypeKeysQueryKey = (params: FindTypeKeys["queryParams"]) =>
  [
    { url: "/v1/client-settings/find-type-keys" },
    ...(params ? [params] : []),
  ] as const
export type FindTypeKeysQueryKey = ReturnType<typeof findTypeKeysQueryKey>
export function findTypeKeysQueryOptions<
  TData = FindTypeKeys["response"],
  TQueryData = FindTypeKeys["response"],
>(
  params: FindTypeKeys["queryParams"],
  options: FindTypeKeys["client"]["parameters"] = {}
): WithRequired<
  UseBaseQueryOptions<
    FindTypeKeys["response"],
    FindTypeKeys["error"],
    TData,
    TQueryData
  >,
  "queryKey"
> {
  const queryKey = findTypeKeysQueryKey(params)
  return {
    queryKey,
    queryFn: async () => {
      const res = await client<FindTypeKeys["data"], FindTypeKeys["error"]>({
        method: "get",
        url: `/v1/client-settings/find-type-keys`,
        params,
        ...options,
      })
      return res
    },
  }
}
/**
 * @summary Find type keys
 * @link /v1/client-settings/find-type-keys
 */
export function useFindTypeKeys<
  TData = FindTypeKeys["response"],
  TQueryData = FindTypeKeys["response"],
  TQueryKey extends QueryKey = FindTypeKeysQueryKey,
>(
  params: FindTypeKeys["queryParams"],
  options: {
    query?: Partial<
      UseBaseQueryOptions<
        FindTypeKeys["response"],
        FindTypeKeys["error"],
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: FindTypeKeys["client"]["parameters"]
  } = {}
): UseQueryResult<TData, FindTypeKeys["error"]> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? findTypeKeysQueryKey(params)
  const query = useQuery<
    FindTypeKeys["data"],
    FindTypeKeys["error"],
    TData,
    any
  >({
    ...findTypeKeysQueryOptions<TData, TQueryData>(params, clientOptions),
    queryKey,
    ...queryOptions,
  }) as UseQueryResult<TData, FindTypeKeys["error"]> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
